// SPDX-FileCopyrightText: 2026 Z-Meet contributors
// SPDX-License-Identifier: Apache-2.0

// Kênh sự kiện thời gian thực — ADR-0006.
//
// ⚠️ Sự kiện là GỢI Ý, không phải dữ liệu. Không có bí mật nào đi qua kênh này:
// khi nghe "bạn đã được duyệt", client tự gọi API để lấy token. Nhờ vậy một sự
// kiện bị mất là VÔ HẠI — client đối soát lại sau mỗi lần nối lại.
//
// Đó cũng là lý do mọi kết nối bắt đầu bằng một sự kiện `resync`: chỉ có ĐÚNG
// MỘT đường mã để lấy trạng thái hiện tại, dùng chung cho lần đầu lẫn lần nối
// lại thứ hai mươi.

import { useEffect, useRef } from 'react'
import { streamTicket } from './api'

export type StreamHandlers = {
  /** Gọi khi kênh mở và sau MỖI lần nối lại. Đây là nơi đối soát trạng thái. */
  onResync: () => void
  onEvent?: (type: string, data: unknown) => void
}

type Identity =
  | { kind: 'guest'; principal: string; participantID: string }
  | { kind: 'member'; workspace: string }

/**
 * useEventStream mở kênh sự kiện của một session.
 *
 * Hai cách chứng minh danh tính vì có hai loại người nghe (ADR-0006 §3):
 * khách dùng guest id — bí mật không đoán được nhận lúc vào phòng — còn thành
 * viên xin một vé 60 giây qua lời gọi đã xác thực, vì EventSource KHÔNG đặt
 * được header Authorization.
 */
export function useEventStream(
  sessionID: string | null,
  identity: Identity | null,
  handlers: StreamHandlers,
) {
  // Handler giữ trong ref: nếu để chúng vào mảng phụ thuộc thì mỗi lần render
  // sẽ đóng và mở lại kênh, và mỗi lần mở lại là một khoảng trống mà sự kiện
  // rơi vào đó biến mất.
  const hRef = useRef(handlers)
  hRef.current = handlers

  useEffect(() => {
    if (!sessionID || !identity) return
    let closed = false
    let source: EventSource | null = null
    let retry = 0
    // ReturnType<typeof setTimeout> chứ không phải number: với @types/node có
    // mặt, setTimeout của trình duyệt và của Node cùng nằm trong phạm vi và
    // Node trả về một Timeout.
    let timer: ReturnType<typeof setTimeout> | undefined

    const open = async () => {
      if (closed) return

      let url: string
      if (identity.kind === 'guest') {
        url =
          `/v1/sessions/${sessionID}/events?as=${encodeURIComponent(identity.principal)}` +
          `&participant=${encodeURIComponent(identity.participantID)}`
      } else {
        const r = await streamTicket(sessionID, identity.workspace)
        if (!r.ok || closed) return
        url = `/v1/sessions/${sessionID}/events?ticket=${encodeURIComponent(r.data.ticket)}`
      }

      const es = new EventSource(url)
      source = es

      // MỘT đường vào duy nhất. Loại sự kiện nằm trong payload, không ở
      // trường `event:` của SSE — nhờ vậy không có danh sách tên nào để quên
      // cập nhật khi server thêm một loại mới.
      es.onmessage = (ev) => {
        let msg: { type?: string; data?: unknown }
        try {
          msg = JSON.parse(ev.data as string) as { type?: string; data?: unknown }
        } catch {
          return // payload hỏng không được làm sập giao diện
        }
        if (msg.type === 'resync') {
          retry = 0
          hRef.current.onResync()
          return
        }
        if (msg.type) hRef.current.onEvent?.(msg.type, msg.data ?? {})
      }

      es.onerror = () => {
        // EventSource TỰ nối lại khi mạng chập; chỉ can thiệp khi nó bỏ cuộc.
        //
        // Nó nối lại tới ĐÚNG URL cũ, mà vé của thành viên chỉ sống 60 giây —
        // nên khi kết nối đóng hẳn, phải mở lại từ đầu để xin vé mới.
        if (es.readyState !== EventSource.CLOSED || closed) return
        es.close()
        source = null
        const wait = Math.min(1000 * 2 ** retry++, 15000)
        timer = setTimeout(open, wait)
      }
    }

    void open()
    return () => {
      closed = true
      clearTimeout(timer)
      source?.close()
    }
  }, [sessionID, identity?.kind, (identity as { principal?: string })?.principal])
}
